class BundleManager {
  constructor() {
    this.mainProductHandle = 'caravan-cargo-electric-bike';
    this.debugMode = true;
    this.log('BundleManager 初始化开始');
    this.init();
  }

  log(message, data = null) {
    if (this.debugMode) {
      console.log(`[BundleManager] ${message}`, data || '');
    }
  }

  error(message, error = null) {
    console.error(`[BundleManager ERROR] ${message}`, error || '');
  }

  init() {
    this.log('开始初始化事件监听器');
    
    // 使用事件委托监听删除按钮点击
    document.addEventListener('click', (e) => {
      // 检查是否点击了删除按钮
      const removeLink = e.target.closest('.cart-remove-item');
      if (removeLink) {
        this.log('检测到删除按钮点击', removeLink);
        e.preventDefault();
        e.stopPropagation();
        this.handleRemoveItem(removeLink);
      }
    });
    
    this.log('事件监听器初始化完成');
  }

  async handleRemoveItem(removeLink) {
    this.log('开始处理删除操作');
    
    const productHandle = removeLink.dataset.productHandle;
    const lineKey = removeLink.dataset.lineKey;
    
    this.log('删除商品信息', {
      productHandle: productHandle,
      lineKey: lineKey,
      isMainProduct: productHandle === this.mainProductHandle
    });
    
    try {
      if (productHandle === this.mainProductHandle) {
        this.log('检测到主产品删除，开始删除bundle配件');
        await this.removeBundleItems(lineKey);
      } else {
        this.log('普通产品删除');
        await this.removeItem(lineKey);
        this.refreshCart();
      }
    } catch (error) {
      this.error('删除操作失败', error);
    }
  }

  async removeBundleItems(mainProductLineKey) {
    try {
      this.log('开始获取购物车数据');
      
      const cartResponse = await fetch('/cart.js');
      if (!cartResponse.ok) {
        throw new Error(`获取购物车失败: ${cartResponse.status}`);
      }
      
      const cart = await cartResponse.json();
      this.log('购物车数据获取成功', cart);
      
      const bundleItems = this.findBundleItems(cart, mainProductLineKey);
      this.log('找到的bundle配件', bundleItems);
      
      const itemsToRemove = [mainProductLineKey, ...bundleItems];
      this.log('准备删除的商品', itemsToRemove);
      
      // 使用批量更新而不是逐个删除
      await this.batchRemoveItems(itemsToRemove);
      
      this.log('所有商品删除完成，刷新购物车');
      this.refreshCart();
      
    } catch (error) {
      this.error('删除bundle产品时出错', error);
      try {
        await this.removeItem(mainProductLineKey);
        this.refreshCart();
      } catch (fallbackError) {
        this.error('备用删除方案也失败', fallbackError);
      }
    }
  }

  // 新增：批量删除方法
  async batchRemoveItems(lineKeys) {
    this.log('开始批量删除商品', lineKeys);
    
    try {
      const updates = {};
      lineKeys.forEach(key => {
        updates[key] = 0;
      });
      
      this.log('批量更新数据', updates);
      
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ updates })
      });
      
      this.log('批量删除响应状态', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        this.error('批量删除失败，响应内容', errorText);
        throw new Error(`批量删除失败: ${response.status} - ${errorText}`);
      }
      
      const result = await response.json();
      this.log('批量删除成功', result);
      return result;
      
    } catch (error) {
      this.error('批量删除出错，尝试逐个删除', error);
      // 如果批量删除失败，回退到逐个删除
      for (const lineKey of lineKeys) {
        try {
          await this.removeItem(lineKey);
          await new Promise(resolve => setTimeout(resolve, 300));
        } catch (itemError) {
          this.error(`删除商品 ${lineKey} 失败`, itemError);
        }
      }
    }
  }

  findBundleItems(cart, mainProductLineKey) {
    const bundleItems = [];
    this.log('开始查找bundle配件', cart.items);
    
    cart.items.forEach((item, index) => {
      this.log(`检查商品 ${index + 1}`, {
        handle: item.handle,
        title: item.product_title,
        properties: item.properties,
        key: item.key
      });
      
      if (item.key === mainProductLineKey) {
        this.log('跳过主产品', item.key);
        return;
      }
      
      let isBundle = false;
      
      // 方法1: 检查line item properties
      if (item.properties) {
        const bundleProps = ['_bundle_id', '_kaching_bundle', 'bundle_parent', '_bundle', 'bundle'];
        for (const prop of bundleProps) {
          if (item.properties[prop]) {
            this.log(`通过属性 ${prop} 识别为bundle配件`, item.properties[prop]);
            isBundle = true;
            break;
          }
        }
      }
      
      // 方法2: 检查产品标题
      if (!isBundle && item.product_title) {
        const bundleKeywords = ['Bundle', 'bundle', '配件', 'Accessory', 'Add-on'];
        for (const keyword of bundleKeywords) {
          if (item.product_title.includes(keyword) || 
              (item.variant_title && item.variant_title.includes(keyword))) {
            this.log(`通过标题关键词 ${keyword} 识别为bundle配件`);
            isBundle = true;
            break;
          }
        }
      }
      
      // 方法3: 检查特定的配件产品handle
      if (!isBundle) {
        const accessoryHandles = [
          'soft-floor-liner',
          'safety-belts',
          'seat-cushions-1',
          'waterproof-rain-cover'
        ];
        
        if (accessoryHandles.includes(item.handle)) {
          this.log(`通过handle识别为bundle配件: ${item.handle}`);
          isBundle = true;
        }
      }
      
      if (isBundle) {
        bundleItems.push(item.key);
        this.log('添加到bundle配件列表', item.key);
      }
    });
    
    this.log('bundle配件查找完成', bundleItems);
    return bundleItems;
  }

  // 修复后的删除方法
  async removeItem(lineKey) {
    this.log('开始删除单个商品', lineKey);
    
    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          updates: {
            [lineKey]: 0
          }
        })
      });
      
      this.log('删除请求响应状态', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        this.error('删除失败，响应内容', errorText);
        throw new Error(`删除商品失败: ${response.status} - ${errorText}`);
      }
      
      const result = await response.json();
      this.log('商品删除成功', result);
      return result;
      
    } catch (error) {
      this.error('删除商品时出错', error);
      throw error;
    }
  }

  refreshCart() {
    this.log('开始刷新购物车显示');
    
    try {
      if (typeof theme !== 'undefined' && theme.cart && theme.cart.buildCart) {
        this.log('使用 theme.cart.buildCart 刷新');
        theme.cart.buildCart();
      }
      else if (typeof window.cartUpdated === 'function') {
        this.log('使用 window.cartUpdated 刷新');
        window.cartUpdated();
      }
      else {
        this.log('使用页面重载刷新');
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }
    } catch (error) {
      this.error('刷新购物车时出错', error);
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    }
  }
}

// 确保DOM完全加载后再初始化
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    console.log('[BundleManager] DOM加载完成，初始化Bundle管理器');
    window.bundleManager = new BundleManager();
  });
} else {
  console.log('[BundleManager] DOM已加载，立即初始化Bundle管理器');
  window.bundleManager = new BundleManager();
}